import { ENGINE_HEIGHT, ENGINE_WIDTH, type FitMode, type SourceKind } from "./types";

export interface SourceFrame {
  source: TexImageSource;
  width: number;
  height: number;
  ready: boolean;
}

export interface SourceManagerCallbacks {
  onChange?: (kind: SourceKind) => void;
  onError?: (kind: SourceKind, error: Error) => void;
}

type ExternalSource = {
  element: HTMLImageElement | HTMLVideoElement | ImageBitmap;
  width: number;
  height: number;
  objectUrl?: string;
  stream?: MediaStream;
};

function asError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function waitForMedia(element: HTMLMediaElement, eventName: "loadeddata" | "loadedmetadata"): Promise<void> {
  return new Promise((resolve, reject) => {
    const done = (): void => {
      cleanup();
      resolve();
    };
    const failed = (): void => {
      cleanup();
      reject(new Error(element.error?.message || "The selected media could not be decoded"));
    };
    const cleanup = (): void => {
      element.removeEventListener(eventName, done);
      element.removeEventListener("error", failed);
    };
    element.addEventListener(eventName, done, { once: true });
    element.addEventListener("error", failed, { once: true });
  });
}

/** Provides decode-safe local sources. A failed replacement never evicts the active source. */
export class SourceManager {
  #canvas: HTMLCanvasElement;
  #context: CanvasRenderingContext2D;
  #kind: SourceKind = "bars";
  #fit: FitMode = "fit";
  #external?: ExternalSource;
  #callbacks: SourceManagerCallbacks;
  #lastDraw = -1;
  #requestId = 0;

  constructor(callbacks: SourceManagerCallbacks = {}) {
    this.#canvas = document.createElement("canvas");
    this.#canvas.width = ENGINE_WIDTH;
    this.#canvas.height = ENGINE_HEIGHT;
    const context = this.#canvas.getContext("2d", { alpha: false });
    if (!context) throw new Error("A 2D canvas context is required for CRTVAR sources");
    this.#context = context;
    this.#callbacks = callbacks;
    this.#drawGenerated(0);
  }

  get kind(): SourceKind {
    return this.#kind;
  }

  get fit(): FitMode {
    return this.#fit;
  }

  setFit(fit: FitMode): void {
    this.#fit = fit;
  }

  getFrame(now = performance.now()): SourceFrame {
    if (this.#kind === "bars" || this.#kind === "grid" || this.#kind === "scene") {
      this.#drawGenerated(now);
      return { source: this.#canvas, width: ENGINE_WIDTH, height: ENGINE_HEIGHT, ready: true };
    }
    const external = this.#external;
    if (!external) return { source: this.#canvas, width: ENGINE_WIDTH, height: ENGINE_HEIGHT, ready: false };
    if (external.element instanceof HTMLVideoElement && external.element.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      return { source: this.#canvas, width: ENGINE_WIDTH, height: ENGINE_HEIGHT, ready: false };
    }
    return { source: external.element, width: external.width, height: external.height, ready: true };
  }

  async setSource(kind: SourceKind, file?: File, deviceId?: string): Promise<void> {
    const requestId = ++this.#requestId;
    try {
      if (kind === "bars" || kind === "grid" || kind === "scene") {
        this.#replaceExternal(undefined);
        this.#kind = kind;
        this.#drawGenerated(performance.now(), true);
      } else if (kind === "image") {
        if (!file) throw new Error("Select an image file before choosing the image source");
        const candidate = await this.#decodeImage(file);
        if (requestId !== this.#requestId) { this.#disposeExternal(candidate); return; }
        this.#replaceExternal(candidate);
        this.#kind = kind;
      } else if (kind === "video") {
        if (!file) throw new Error("Select a video file before choosing the video source");
        const candidate = await this.#decodeVideo(file);
        if (requestId !== this.#requestId) { this.#disposeExternal(candidate); return; }
        this.#replaceExternal(candidate);
        this.#kind = kind;
      } else {
        const candidate = await this.#openCamera(deviceId);
        if (requestId !== this.#requestId) { this.#disposeExternal(candidate); return; }
        this.#replaceExternal(candidate);
        this.#kind = "camera";
      }
      if (requestId === this.#requestId) this.#callbacks.onChange?.(this.#kind);
    } catch (error) {
      const converted = asError(error);
      if (requestId === this.#requestId) this.#callbacks.onError?.(kind, converted);
      throw converted;
    }
  }

  async listCameras(): Promise<readonly MediaDeviceInfo[]> {
    if (!navigator.mediaDevices?.enumerateDevices) return [];
    return (await navigator.mediaDevices.enumerateDevices()).filter((device) => device.kind === "videoinput");
  }

  #drawGenerated(now: number, force = false): void {
    if (!force && this.#kind !== "scene" && this.#lastDraw >= 0) return;
    if (!force && this.#kind === "scene" && now - this.#lastDraw < 16) return;
    this.#lastDraw = now;
    const ctx = this.#context;
    if (this.#kind === "grid") this.#drawGrid(ctx);
    else if (this.#kind === "scene") this.#drawScene(ctx, now / 1000);
    else this.#drawBars(ctx);
  }

  #drawBars(ctx: CanvasRenderingContext2D): void {
    const top = ["#bfbfbf", "#bfbf00", "#00bfbf", "#00bf00", "#bf00bf", "#bf0000", "#0000bf"];
    const width = ENGINE_WIDTH / top.length;
    for (let i = 0; i < top.length; i += 1) {
      ctx.fillStyle = top[i]!;
      ctx.fillRect(i * width, 0, Math.ceil(width), ENGINE_HEIGHT * 0.68);
    }
    const middle = ["#0000bf", "#111111", "#bf00bf", "#111111", "#00bfbf", "#111111", "#bfbfbf"];
    for (let i = 0; i < middle.length; i += 1) {
      ctx.fillStyle = middle[i]!;
      ctx.fillRect(i * width, ENGINE_HEIGHT * 0.68, Math.ceil(width), ENGINE_HEIGHT * 0.08);
    }
    const lower = ["#00143d", "#ffffff", "#32006a", "#111111", "#090909", "#1d1d1d"];
    const lowerWidth = ENGINE_WIDTH / lower.length;
    for (let i = 0; i < lower.length; i += 1) {
      ctx.fillStyle = lower[i]!;
      ctx.fillRect(i * lowerWidth, ENGINE_HEIGHT * 0.76, Math.ceil(lowerWidth), ENGINE_HEIGHT * 0.24);
    }
  }

  #drawGrid(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = "#060806";
    ctx.fillRect(0, 0, ENGINE_WIDTH, ENGINE_HEIGHT);
    ctx.strokeStyle = "#d9efe0";
    ctx.lineWidth = 1;
    for (let x = 0; x <= ENGINE_WIDTH; x += 32) {
      ctx.beginPath(); ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, ENGINE_HEIGHT); ctx.stroke();
    }
    for (let y = 0; y <= ENGINE_HEIGHT; y += 32) {
      ctx.beginPath(); ctx.moveTo(0, y + 0.5); ctx.lineTo(ENGINE_WIDTH, y + 0.5); ctx.stroke();
    }
    ctx.strokeStyle = "#49ef8d";
    ctx.lineWidth = 2;
    ctx.strokeRect(16, 16, ENGINE_WIDTH - 32, ENGINE_HEIGHT - 32);
    ctx.beginPath();
    ctx.moveTo(ENGINE_WIDTH / 2, 0); ctx.lineTo(ENGINE_WIDTH / 2, ENGINE_HEIGHT);
    ctx.moveTo(0, ENGINE_HEIGHT / 2); ctx.lineTo(ENGINE_WIDTH, ENGINE_HEIGHT / 2);
    ctx.stroke();
    for (const radius of [64, 128, 192]) {
      ctx.beginPath(); ctx.arc(ENGINE_WIDTH / 2, ENGINE_HEIGHT / 2, radius, 0, TAU); ctx.stroke();
    }
  }

  #drawScene(ctx: CanvasRenderingContext2D, time: number): void {
    const gradient = ctx.createLinearGradient(0, 0, 0, ENGINE_HEIGHT);
    gradient.addColorStop(0, "#061426");
    gradient.addColorStop(0.55, "#3d1a54");
    gradient.addColorStop(1, "#fd6e45");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, ENGINE_WIDTH, ENGINE_HEIGHT);
    ctx.fillStyle = "#ffd877";
    ctx.beginPath();
    ctx.arc(470, 122, 62, 0, TAU);
    ctx.fill();
    const horizon = 305;
    ctx.fillStyle = "#090b18";
    ctx.beginPath();
    ctx.moveTo(0, horizon);
    for (let x = 0; x <= ENGINE_WIDTH; x += 32) {
      ctx.lineTo(x, horizon - 26 - 40 * Math.sin(x * 0.017 + 1.2));
    }
    ctx.lineTo(ENGINE_WIDTH, ENGINE_HEIGHT); ctx.lineTo(0, ENGINE_HEIGHT); ctx.fill();
    ctx.strokeStyle = "rgba(78,255,187,.72)";
    ctx.lineWidth = 1;
    for (let y = horizon; y < ENGINE_HEIGHT; y += 14) {
      const offset = (time * 22) % 14;
      ctx.beginPath(); ctx.moveTo(0, y + offset); ctx.lineTo(ENGINE_WIDTH, y + offset); ctx.stroke();
    }
    for (let x = -10; x <= 10; x += 1) {
      ctx.beginPath();
      ctx.moveTo(ENGINE_WIDTH / 2, horizon);
      ctx.lineTo(ENGINE_WIDTH / 2 + x * 70, ENGINE_HEIGHT);
      ctx.stroke();
    }
    const scanX = ((time * 120) % (ENGINE_WIDTH + 120)) - 60;
    ctx.fillStyle = "rgba(255,255,255,.8)";
    ctx.fillRect(scanX, 42, 2, 220);
    ctx.font = "700 24px ui-monospace, monospace";
    ctx.fillText("CRTVAR // TEST FEED", 24, 42);
  }

  async #decodeImage(file: File): Promise<ExternalSource> {
    if (!file.type.startsWith("image/")) throw new Error("The selected file is not an image");
    if (typeof createImageBitmap === "function") {
      const bitmap = await createImageBitmap(file);
      return { element: bitmap, width: bitmap.width, height: bitmap.height };
    }
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.decoding = "async";
    image.src = objectUrl;
    try {
      await image.decode();
      return { element: image, width: image.naturalWidth, height: image.naturalHeight, objectUrl };
    } catch (error) {
      URL.revokeObjectURL(objectUrl);
      throw error;
    }
  }

  async #decodeVideo(file: File): Promise<ExternalSource> {
    if (!file.type.startsWith("video/")) throw new Error("The selected file is not a video");
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = "auto";
    video.src = objectUrl;
    try {
      await waitForMedia(video, "loadeddata");
      await video.play();
      return { element: video, width: video.videoWidth, height: video.videoHeight, objectUrl };
    } catch (error) {
      URL.revokeObjectURL(objectUrl);
      video.src = "";
      throw error;
    }
  }

  async #openCamera(deviceId?: string): Promise<ExternalSource> {
    if (!navigator.mediaDevices?.getUserMedia) throw new Error("Camera capture is not supported by this browser");
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: deviceId ? { deviceId: { exact: deviceId } } : true,
    });
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    try {
      await waitForMedia(video, "loadedmetadata");
      await video.play();
      return { element: video, width: video.videoWidth, height: video.videoHeight, stream };
    } catch (error) {
      stream.getTracks().forEach((track) => track.stop());
      throw error;
    }
  }

  #replaceExternal(next?: ExternalSource): void {
    const previous = this.#external;
    this.#external = next;
    if (previous) this.#disposeExternal(previous);
  }

  #disposeExternal(source: ExternalSource): void {
    if (source.element instanceof HTMLVideoElement) {
      source.element.pause();
      source.element.srcObject = null;
      source.element.removeAttribute("src");
      source.element.load();
    } else if (typeof ImageBitmap !== "undefined" && source.element instanceof ImageBitmap) {
      source.element.close();
    }
    source.stream?.getTracks().forEach((track) => track.stop());
    if (source.objectUrl) URL.revokeObjectURL(source.objectUrl);
  }

  dispose(): void {
    this.#requestId += 1;
    this.#replaceExternal(undefined);
  }
}

const TAU = Math.PI * 2;
